import React, { useCallback, useState } from "react";
import { getCurrentTimeZone } from "../../../utils/dateutils";
import apiService from "../../../service/apiservice/apiService";
import ErrorNotificationComponent from "../../../components/generic/error/ErrorNotificationComponent";
import { CLICKABLE_REPORT_CATEGORY_KEYS } from "./constants";

const CLICKABLE_CATEGORIES_SET = new Set(CLICKABLE_REPORT_CATEGORY_KEYS);

/**
 * Hook for cell-click workforce detail modal: state, handler, and column enhancer.
 */
export function useCellDetailModal(
  organizationIdentifier,
  teams,
  year,
  months,
  quarters,
) {
  const [cellModalOpen, setCellModalOpen] = useState(false);
  const [cellModalLoading, setCellModalLoading] = useState(false);
  const [cellModalData, setCellModalData] = useState(null);
  const [cellModalTableKey, setCellModalTableKey] = useState(null);
  const [cellModalCategoryKey, setCellModalCategoryKey] = useState(null);
  const [cellModalMonthNumber, setCellModalMonthNumber] = useState(null);
  const [cellModalQuarterNumber, setCellModalQuarterNumber] = useState(null);

  const handleCellClick = useCallback(
    async (record, columnMeta, cellValue) => {
      if (cellValue === 0 || cellValue === "0") return;

      const { monthNumber, quarterNumber, tableKey } = columnMeta;
      setCellModalOpen(true);
      setCellModalLoading(true);
      setCellModalData(null);
      setCellModalTableKey(tableKey);
      setCellModalCategoryKey(record?.value ?? null);
      setCellModalMonthNumber(monthNumber ?? null);
      setCellModalQuarterNumber(quarterNumber ?? null);

      try {
        const endpoint =
          "niramayafoundation/niramayareporting/v1/report/entities";
        const params = {
          organizationIdentifier,
          tableName: tableKey,
          category: record.value,
          timezone: getCurrentTimeZone(),
          year,
          month: monthNumber,
          quarter: quarterNumber,
          teamIds: teams.map((team) => team?.id)?.join(","),
        };
        const response = await apiService.get(endpoint, params);
        setCellModalData(response?.data ?? response);
      } catch (error) {
        ErrorNotificationComponent(error);
        setCellModalOpen(false);
      } finally {
        setCellModalLoading(false);
      }
    },
    [organizationIdentifier, teams, year],
  );

  const getColumnsWithCellClick = useCallback(
    (columns, tableKey) =>
      columns.map((col) => {
        const columnKey = col.dataIndex ?? col.key;
        const isFirstColumn = columnKey === "type";
        const isTotalColumn = columnKey === "total";

        if (isFirstColumn || isTotalColumn) {
          return { ...col };
        }

        const baseOnCell = col.onCell;
        const baseRender = col.render;
        const monthName = columnKey;
        const monthNumber =
          months.indexOf(monthName) >= 0
            ? months.indexOf(monthName) + 1
            : undefined;
        const quarterNumber =
          quarters.indexOf(monthName) >= 0
            ? quarters.indexOf(monthName) + 1
            : undefined;
        const columnMeta = { monthNumber, quarterNumber, tableKey };

        return {
          ...col,
          onCell: (record, rowIndex) => {
            const baseProps =
              typeof baseOnCell === "function"
                ? baseOnCell(record, rowIndex)
                : {};
            const isClickable = CLICKABLE_CATEGORIES_SET.has(record?.value);
            if (!isClickable) {
              return baseProps;
            }
            return {
              ...baseProps,
              className: [
                baseProps.className,
                "reporting-log-cell--clickable",
              ]
                .filter(Boolean)
                .join(" "),
              style: { ...baseProps.style, cursor: "pointer" },
            };
          },
          render: (text, record, index) => {
            const content = baseRender ? baseRender(text, record, index) : text;
            const isClickable = CLICKABLE_CATEGORIES_SET.has(record?.value);
            if (!isClickable) {
              return content;
            }
            return (
              <div
                role="button"
                tabIndex={0}
                className="reporting-log-cell--clickable"
                onClick={() => handleCellClick(record, columnMeta, text)}
                onKeyDown={(e) => {
                  if (e.key === "Enter" || e.key === " ") {
                    e.preventDefault();
                    handleCellClick(record, columnMeta, text);
                  }
                }}
              >
                {content}
              </div>
            );
          },
        };
      }),
    [months, quarters, handleCellClick],
  );

  return {
    cellModalOpen,
    setCellModalOpen,
    cellModalLoading,
    cellModalData,
    cellModalTableKey,
    cellModalCategoryKey,
    cellModalMonthNumber,
    cellModalQuarterNumber,
    handleCellClick,
    getColumnsWithCellClick,
  };
}
